import * as React from 'react';
import { Button, Icon, PopupTooltip } from '@teamsnap/teamsnap-ui';

import FlexPillButton from './FlexPillButton';
import ConfirmDeletePopup from './ConfirmDeletePopup';
import { Toggle } from '@teamsnap/snap-ui';

interface Waiver {
  id: number;
  title: string;
  content: string;
  active: boolean;
  hasFormWaiverResults?: boolean;
}

interface Props {
  formId: number;
  index: number;
  waiver: Waiver;
  setActiveWaiverIndex: (index: number) => void;
  setDrawerOpen: (isOpen: boolean) => void;
  deleteWaiver: (mutation) => void;
  setWaivers: (stateFn) => void;
  handleWaiverUpdate: (w: Waiver) => void;
}

const WaiverPillButton: React.FC<Props> = ({
  formId,
  index,
  waiver,
  setActiveWaiverIndex,
  setDrawerOpen,
  deleteWaiver,
  setWaivers,
  handleWaiverUpdate,
}) => {
  const popupRef = React.useRef<HTMLDivElement>();
  const [isPopupOpen, setIsPopupOpen] = React.useState(false);
  const onPopupCancel = () => setIsPopupOpen(false);
  const onPopupDelete = async () => {
    if (waiver.id !== null) {
      await deleteWaiver({ variables: { formId, id: waiver.id } });
    }
    setWaivers(index);
    setIsPopupOpen(false);
  };

  const handleToggleClick = async (e) => {
    e.stopPropagation();

    await handleWaiverUpdate({ ...waiver, active: !waiver.active });
  };

  const hasFormWaiverResults = waiver.hasFormWaiverResults;

  const toggleTooltipText =
    'If turned off, this digital agreement will be hidden from future registrants. Past registrants won\'t be affected. You can turn the digital agreement back on.';

  const deleteButtonTooltipText = hasFormWaiverResults
    ? 'Digital agreements that have registrants cannot be deleted.'
    : 'Once deleted, this digital agreement cannot be restored.';

  return (
    <FlexPillButton
      key={index}
      onClick={(e) => {
        const isClickInsidePopup =
          popupRef.current && (popupRef.current === e.target || popupRef.current.contains(e.target));

        if (!isClickInsidePopup) {
          setActiveWaiverIndex(index);
          setDrawerOpen(true);
        }
      }}
    >
      <div className="u-size1of1 u-flex u-flexJustifyBetween sui-items-center">
        <p className="sui-truncate" data-testid={`waiver-title-${index}`}>
          {waiver.title}
        </p>

        <div className="sui-flex">
          <PopupTooltip text={toggleTooltipText} mods="u-fontSizeMd sui-pt-0.5">
            <Toggle
              data-testid="WaiverPillButton--toggle"
              on={waiver.active}
              name={`WaiverPillButton--toggle-${waiver.id}`}
              onClick={(e) => handleToggleClick(e)}
            />
          </PopupTooltip>

          <PopupTooltip text={deleteButtonTooltipText} mods="u-fontSizeMd">
            <Button
              isDisabled={hasFormWaiverResults}
              mods="u-flex u-flexJustifyEnd u-flexAlignItemsCenter"
              type="button"
              style={{ padding: 0, borderWidth: 0, background: 'transparent' }}
              onClick={(e) => {
                e.stopPropagation();

                setIsPopupOpen(true);
              }}
            >
              <Icon
                mods={`u-flex ${hasFormWaiverResults ? 'sui-text-gray-60' : 'sui-text-red-5'}`}
                name="trash"
                style={{ width: 24, height: 24, cursor: hasFormWaiverResults ? 'not-allowed' : 'pointer' }}
              />
            </Button>
          </PopupTooltip>

          <ConfirmDeletePopup ref={popupRef} isOpen={isPopupOpen} onCancel={onPopupCancel} onDelete={onPopupDelete} />
        </div>
      </div>
    </FlexPillButton>
  );
};

export default WaiverPillButton;
